import ExcelJS from "exceljs";
import { isEmpty } from "lodash";
import type { ChangeEventHandler, FC, MouseEventHandler, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { ExcelTemplateType, getTemplate } from "./excel-template";
import { useExcelTemplate } from "./hooks/useExcelTemplate";

interface Props {
  accept?: string;
  template: ExcelTemplateType;
  disabled?: boolean;
  onChange?: (newRows: unknown[]) => void;
  onClick?: MouseEventHandler<HTMLInputElement>;
  children?: ReactNode;
}

const EXCEL_FILE = ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";

const UploadFile: FC<Props> = (props) => {
  const { accept = EXCEL_FILE, template, disabled, onChange, onClick } = props;
  const { importFile } = useExcelTemplate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [loadFile, setLoadFile] = useState(false);
  const onChangeRef = useRef(onChange);

  const handleFileUpload: ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setFile(file);
  };

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!file) {
      return;
    }

    const reader = new FileReader();

    const handleUpload = async (evt: ProgressEvent<FileReader>) => {
      setLoadFile(true);
      try {
        /**
         * Handle import file & validate fields
         */
        const data = await importFile({
          buffer: evt.target?.result as ExcelJS.Buffer,
          template: getTemplate(template),
        });

        if (!data || isEmpty(data)) {
          return;
        }

        /**
         * bindingData
         */
        onChangeRef.current?.(data);

        setFile(undefined);
        clearInputRefValue();
      } catch (error) {
        console.error(error);
      } finally {
        setLoadFile(false);
      }
    };

    reader.readAsArrayBuffer(file);

    reader.addEventListener("load", handleUpload);

    return () => {
      reader.removeEventListener("load", handleUpload);
    };
  }, [file, importFile, template]);

  const clearInputRefValue = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const isDisabled = loadFile || disabled;

  return (
    <label className="btnUpload" aria-disabled={isDisabled}>
      {loadFile ? "Đang tải..." : "Tải biểu mẫu"}
      {/* {children} */}
      <input
        ref={inputRef}
        disabled={isDisabled}
        hidden
        type="file"
        accept={accept}
        multiple={false}
        onChange={handleFileUpload}
        onClick={onClick}
      />
    </label>
  );
};

export default UploadFile;
